import { useLayoutEffect, useRef, useState } from "react";

import type { FC, ReactNode, RefObject } from "react";

const GAP = 8;
const PADDING = 8;

type ChartHoverTooltipProps = {
  anchor: { left: number; top: number };
  children: ReactNode;
  containerRef: RefObject<HTMLDivElement | null>;
};

function getTooltipTransform(
  anchor: { left: number; top: number },
  tooltipSize: { height: number; width: number },
  containerSize: { height: number; width: number }
) {
  const { height: th, width: tw } = tooltipSize;
  const { height: ch, width: cw } = containerSize;

  let translateX = "-50%";
  let translateY = `calc(-100% - ${GAP}px)`;

  const centeredLeft = anchor.left - tw / 2;
  const centeredRight = anchor.left + tw / 2;

  if (centeredRight > cw - PADDING) {
    translateX = `calc(-100% - ${GAP}px)`;
  } else if (centeredLeft < PADDING) {
    translateX = `${GAP}px`;
  }

  const aboveTop = anchor.top - th - GAP;
  if (aboveTop < PADDING) {
    translateY = `${GAP}px`;
  }

  const belowBottom = anchor.top + GAP + th;
  if (belowBottom > ch - PADDING && aboveTop >= PADDING) {
    translateY = `calc(-100% - ${GAP}px)`;
  }

  return `translate(${translateX}, ${translateY})`;
}

export const ChartHoverTooltip: FC<ChartHoverTooltipProps> = ({
  anchor,
  children,
  containerRef,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState(`translate(-50%, calc(-100% - ${GAP}px))`);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    if (!container || !tooltip) return;

    setTransform(
      getTooltipTransform(
        anchor,
        { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        { width: container.clientWidth, height: container.clientHeight }
      )
    );
  }, [anchor, children, containerRef]);

  return (
    <div
      ref={tooltipRef}
      style={{
        left: anchor.left,
        pointerEvents: "none",
        position: "absolute",
        top: anchor.top,
        transform,
        zIndex: 5,
      }}
    >
      {children}
    </div>
  );
};
